// src/pages/MaidenheadTaxi.jsx
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import PostcodeQuotenew from "./PostcodeQuotenew";
import LiveStrip from "../components/LiveStrip";
import FloatingSupportChat from "../components/FloatingSupportChat";
import PremiumContactStrip from "../components/PremiumContactStrip";
import CcgPremiumReviews from "../components/CcgPremiumReviews";
import CcgRouteLhrStatusCompact from "../components/CcgRouteLhrStatusCompact";
import "./CcgRoutePremium.css";
import "../styles/maidenhead-taxi.css";

const seoTitle = "Maidenhead Taxi & Heathrow Transfers | CheapCabGrab";
const seoDescription = "Book reliable Maidenhead taxis for Heathrow, Gatwick, Luton, Stansted and local SL6 journeys. Fixed-price fares, licensed drivers, MPV & minibus options, 24/7 support.";
const seoCanonical = "https://www.cheapcabgrab.com/maidenhead-taxi";
const seoKeywords = "Maidenhead taxi, SL6 taxi, Maidenhead to Heathrow taxi, Heathrow transfers from Maidenhead, fixed price taxi Maidenhead, airport taxi Maidenhead, CheapCabGrab";
const ogTitle = "Maidenhead Taxi – Fixed-Price Heathrow Transfers & Local SL6 Cabs";
const ogDescription = "Book a CheapCabGrab taxi from Maidenhead, Bray, Cookham & Taplow to Heathrow and all major London airports. Fixed fares, no surge pricing, licensed drivers and airport specialists.";

const maidenheadTaxiJsonLd = {
  "@context": "https://schema.org",
  "@type": "TaxiService",
  name: "CheapCabGrab – Maidenhead Taxi Service",
  url: seoCanonical,
  areaServed: ["Maidenhead SL6", "Bray", "Taplow", "Cookham", "Holyport", "Furze Platt", "Cox Green", "Pinkneys Green", "White Waltham"],
  serviceArea: { "@type": "Place", name: "Maidenhead, Bray, Cookham and Heathrow corridor" },
  sameAs: ["https://www.cheapcabgrab.com/", "https://www.cheapcabgrab.com/heathrow-taxi", "https://www.cheapcabgrab.com/slough-taxi"],
};

const maidenheadFaqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Do you pick up from Maidenhead station?", acceptedAnswer: { "@type": "Answer", text: "Yes – we regularly collect from Maidenhead station, especially for airport transfers and hotel connections." } },
    { "@type": "Question", name: "Can you handle early morning Heathrow flights?", acceptedAnswer: { "@type": "Answer", text: "Yes – CheapCabGrab runs 24/7. Early-morning pickups from Maidenhead for Heathrow and other airports are common." } },
    { "@type": "Question", name: "Do you offer larger vehicles for groups?", acceptedAnswer: { "@type": "Answer", text: "Yes – MPVs, 7–9 seaters and 14+ seater vehicles are available. For bigger groups see our minibus and coach hire pages." } },
  ],
};

const MaidenheadTaxi = () => {
  const scrollToForm = () => {
    const el = document.getElementById("maidenhead-quote-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const setMeta = (selector, attrs) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        document.head.appendChild(element);
      }
      Object.entries(attrs).forEach(([key, value]) => element.setAttribute(key, value));
    };
    const setJsonLd = (id, data) => {
      let script = document.querySelector(`script[data-maidenhead-taxi-jsonld="${id}"]`);
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-maidenhead-taxi-jsonld", id);
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };
    const applySeo = () => {
      document.title = seoTitle;
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = seoCanonical;
      setMeta('meta[name="description"]', { name: "description", content: seoDescription });
      setMeta('meta[name="keywords"]', { name: "keywords", content: seoKeywords });
      setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
      setMeta('meta[property="og:title"]', { property: "og:title", content: ogTitle });
      setMeta('meta[property="og:description"]', { property: "og:description", content: ogDescription });
      setMeta('meta[property="og:url"]', { property: "og:url", content: seoCanonical });
      setMeta('meta[property="og:image"]', { property: "og:image", content: "https://www.cheapcabgrab.com/og-image.jpg" });
      setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
      setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: seoTitle });
      setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: seoDescription });
      setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: "https://www.cheapcabgrab.com/og-image.jpg" });
      setJsonLd("taxi-service", maidenheadTaxiJsonLd);
      setJsonLd("faq", maidenheadFaqJsonLd);
    };
    applySeo();
    const timers = [window.setTimeout(applySeo, 0), window.setTimeout(applySeo, 750)];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  return (
    <div className="maidenhead-page route-page ccg-route-premium ccg-maidenhead-premium">
      <Helmet>
        <title>{seoTitle}</title>
        <meta
          name="description"
          content="Book reliable Maidenhead taxis for Heathrow, Gatwick, Luton, Stansted and local SL6 journeys. Fixed-price fares, licensed drivers, MPV & minibus options, 24/7 support."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/maidenhead-taxi" />
        <meta
          name="keywords"
          content={seoKeywords}
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={ogTitle}
        />
        <meta
          property="og:description"
          content={ogDescription}
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/maidenhead-taxi" />
        <meta property="og:image" content="https://www.cheapcabgrab.com/og-image.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://www.cheapcabgrab.com/og-image.jpg" />
        <script type="application/ld+json" data-maidenhead-taxi-jsonld="taxi-service">{JSON.stringify(maidenheadTaxiJsonLd)}</script>
        <script type="application/ld+json" data-maidenhead-taxi-jsonld="faq">{JSON.stringify(maidenheadFaqJsonLd)}</script>
      </Helmet>

      <LiveStrip variant="maidenhead" />

      <main className="maidenhead-main route-main">
        {/* 🟦 HERO */}
        <section className="ccg-route-hero ccg-maidenhead-hero">
          <div className="ccg-route-hero-shell">
            <div className="ccg-route-hero-top">
              <div className="ccg-route-hero-copy">
                <span className="ccg-route-eyebrow">Maidenhead taxi service</span>
                <h1>Maidenhead Taxi – Heathrow &amp; SL6 Transfers</h1>
                <p>Fixed-price cabs across Maidenhead, Bray, Cookham &amp; Taplow</p>
                <p>
                  Book reliable taxis from Maidenhead to Heathrow, London and all major airports.
                  Fixed upfront fares, licensed drivers and secure online booking with luggage-ready vehicles.
                </p>
              </div>
              <div className="ccg-route-trust-cards" aria-label="CheapCabGrab Maidenhead taxi trust signals">
                <article className="ccg-route-trust-card"><b>✓</b><span><small>Licensed operator</small><strong>Licensed &amp; insured</strong><span>Professional airport transfers</span></span></article>
                <article className="ccg-route-trust-card"><b>G</b><span><small>Google reviews</small><strong>4.8 ★★★★★</strong><span>Trusted by local passengers</span></span></article>
              </div>
            </div>

            <div className="ccg-route-quote-wrap" id="maidenhead-quote-form">
              <div className="ccg-route-quote-head">
                <span className="ccg-route-quote-label">Get instant Maidenhead taxi quotes</span>
                <span className="ccg-route-quote-note"><i /> Free - Fast - No obligation</span>
              </div>
              <PostcodeQuotenew presetPickup="Maidenhead, SL6, UK" />
              <ul className="ccg-route-quote-promises"><li>Fixed upfront fare</li><li>Licensed drivers</li><li>Secure online booking</li><li>Instant confirmation</li></ul>
              <PremiumContactStrip />
            </div>

            <ul className="ccg-route-flags">
              <li>Heathrow, Gatwick, Luton &amp; Stansted specialists</li>
              <li>Fixed prices — no surge pricing</li>
              <li>Meet &amp; greet, flight tracking, licensed drivers</li>
              <li>Saloon, MPV, 8–16 seat minibus &amp; coach options</li>
            </ul>

            <div className="ccg-route-review-strip">
              <CcgPremiumReviews pageKey="/maidenhead-taxi" ariaLabel="Maidenhead taxi customer reviews" />
            </div>
          </div>
        </section>

        <CcgRouteLhrStatusCompact title="Live Heathrow checks for Maidenhead airport taxis" />

        {/* FLOATING CTA */}
        <button className="route-floating-book-btn" onClick={scrollToForm}>
          Book Maidenhead Taxi
        </button>

        {/* MAIDENHEAD AT A GLANCE */}
        <section className="maidenhead-section" id="maidenhead-areas">
          <header className="maidenhead-section-header">
            <h2>🏙️ Maidenhead at a Glance (SL6 & Nearby)</h2>
            <p>
              CheapCabGrab covers Maidenhead town and nearby villages along the Thames and
              M4 corridor.
            </p>
          </header>
          <div className="maidenhead-two-col">
            <div className="maidenhead-info-card">
              <h3>Maidenhead Town Centre (SL6)</h3>
              <ul>
                <li>🏢 Shops, restaurants and hotels around the A4 corridor</li>
                <li>🚆 Station with fast services into London Paddington & Elizabeth Line</li>
                <li>🚕 Regular taxis to Heathrow, London & local business parks</li>
              </ul>
            </div>
            <div className="maidenhead-info-card">
              <h3>Bray & Riverside Dining</h3>
              <ul>
                <li>🍽️ Famous for fine-dining restaurants and riverside pubs</li>
                <li>🚖 Popular taxi routes for evening bookings and group meals</li>
                <li>🚌 Minibuses available for larger party bookings</li>
              </ul>
            </div>
            <div className="maidenhead-info-card">
              <h3>Cookham, Bourne End & Taplow</h3>
              <ul>
                <li>🌳 Thames-side villages and countryside walks</li>
                <li>🚗 Short transfers to Maidenhead station & hotels</li>
                <li>🛫 Taxi connections to Heathrow and other airports</li>
              </ul>
            </div>
            <div className="maidenhead-info-card">
              <h3>Holyport, Cox Green & Surrounding Areas</h3>
              <ul>
                <li>🏡 Residential areas south of Maidenhead</li>
                <li>🚗 Convenient access to M4 and Heathrow</li>
                <li>🚕 School runs, business trips and airport transfers</li>
              </ul>
            </div>
          </div>
        </section>

        {/* VEHICLES SECTION – LINK TO /fleet */}
        <section className="maidenhead-section" id="maidenhead-vehicles">
          <header className="maidenhead-section-header">
            <h2>🚘 Our Maidenhead Taxi Vehicles</h2>
            <p>
              From solo business trips to family holidays and group events, CheapCabGrab’s
              vehicle range covers most types of Maidenhead journeys.
            </p>
          </header>
          <div className="maidenhead-vehicle-grid">
            <article className="maidenhead-vehicle-card">
              <h3>Saloon</h3>
              <p>Best for individuals or couples with hand luggage or 2–3 medium cases.</p>
              <ul>
                <li>👥 Up to 3 passengers</li>
                <li>🧳 2–3 medium suitcases</li>
                <li>🛫 Typical: Maidenhead ↔ Heathrow / London</li>
              </ul>
            </article>
            <article className="maidenhead-vehicle-card">
              <h3>Estate / MPV</h3>
              <p>Extra space for families, longer stays or more luggage.</p>
              <ul>
                <li>👥 Up to 4 passengers</li>
                <li>🧳 Large boot space</li>
                <li>🏨 Ideal for hotel & apartment transfers</li>
              </ul>
            </article>
            <article className="maidenhead-vehicle-card">
              <h3>7–9 Seater Minibus</h3>
              <p>
                Great for family groups, airport runs with lots of luggage or nights out in
                London.
              </p>
              <ul>
                <li>👥 Up to 7–9 passengers</li>
                <li>🧳 Ample luggage room</li>
                <li>🚌 Available for airports and local events</li>
              </ul>
            </article>
            <article className="maidenhead-vehicle-card">
              <h3>14-Seater & Coach</h3>
              <p>
                For larger groups, weddings, corporate outings and school trips around
                Maidenhead and Heathrow.
              </p>
              <ul>
                <li>👥 14+ passengers</li>
                <li>📅 Pre-booking required</li>
                <li>
                  🤝 Operated in partnership with our PSV partner RHP Minibus Hire UK Limited
                  for coach services.
                </li>
              </ul>
            </article>
          </div>
          <p className="maidenhead-fleet-link">
            See full vehicle details in our <Link to="/fleet">CheapCabGrab fleet guide</Link>.
          </p>
        </section>

        {/* TYPICAL FARES */}
        <section className="maidenhead-section" id="maidenhead-fares">
          <header className="maidenhead-section-header">
            <h2>💷 Typical Maidenhead Taxi Fares & Journey Times</h2>
            <p>
              These are guide prices for standard saloon vehicles. Your exact fixed fare will
              be shown clearly in the booking form before you confirm.
            </p>
          </header>
          <div className="maidenhead-fares-grid">
            <article className="maidenhead-fare-card">
              <h3>Maidenhead → Heathrow</h3>
              <ul>
                <li>📍 Around 14–20 miles (route dependent)</li>
                <li>⏱ 20–35 minutes</li>
                <li>💷 From <strong>£45–£65</strong></li>
              </ul>
            </article>
            <article className="maidenhead-fare-card">
              <h3>Maidenhead → Gatwick</h3>
              <ul>
                <li>📍 Around 50–60 miles</li>
                <li>⏱ 60–90 minutes</li>
                <li>💷 From <strong>£100–£130</strong></li>
              </ul>
            </article>
            <article className="maidenhead-fare-card">
              <h3>Maidenhead → Luton</h3>
              <ul>
                <li>📍 Around 40–50 miles</li>
                <li>⏱ 50–80 minutes</li>
                <li>💷 From <strong>£80–£105</strong></li>
              </ul>
            </article>
            <article className="maidenhead-fare-card">
              <h3>Maidenhead → Stansted</h3>
              <ul>
                <li>📍 Around 65–75 miles</li>
                <li>⏱ 80–120 minutes</li>
                <li>💷 From <strong>£130–£170</strong></li>
              </ul>
            </article>
          </div>
          <p className="maidenhead-fares-note">
            For live pricing, please use the quote form above or start a new search on the{" "}
            <Link to="/">CheapCabGrab homepage</Link>.
          </p>
        </section>

        {/* ATTRACTIONS NEAR MAIDENHEAD */}
        <section className="maidenhead-section" id="maidenhead-attractions">
          <header className="maidenhead-section-header">
            <h2>🏞️ Attractions Near Maidenhead</h2>
            <p>
              Maidenhead sits on the River Thames with easy access to countryside walks and
              nearby attractions. CheapCabGrab provides taxis and minibuses to the main
              highlights.
            </p>
          </header>
          <div className="maidenhead-attractions-grid">
            <article className="maidenhead-attraction-card">
              <h3>Windsor & Eton</h3>
              <ul>
                <li>📍 Around 5–7 miles from Maidenhead</li>
                <li>⏱ 15–25 minutes by taxi</li>
                <li>💷 Typical fare: £20–£35 (saloon)</li>
              </ul>
            </article>
            <article className="maidenhead-attraction-card">
              <h3>Cliveden House & Gardens</h3>
              <ul>
                <li>📍 Country estate north of Maidenhead</li>
                <li>⏱ 10–20 minutes by taxi</li>
                <li>💷 Typical fare: £15–£30 (saloon)</li>
              </ul>
            </article>
            <article className="maidenhead-attraction-card">
              <h3>Bray & Riverside Restaurants</h3>
              <ul>
                <li>📍 Just south of Maidenhead</li>
                <li>🍽️ Popular for special-occasion dining</li>
                <li>🚌 Minibus options for groups and tasting menus</li>
              </ul>
            </article>
          </div>
          <p className="maidenhead-attractions-link">
            For more nearby ideas, see our{" "}
            <Link to="/heathrow-attractions">Heathrow & Windsor attractions guide</Link>.
          </p>
        </section>

        {/* HOTELS NEAR MAIDENHEAD & HEATHROW */}
        <section className="maidenhead-section" id="maidenhead-hotels">
          <header className="maidenhead-section-header">
            <h2>🏨 Hotels in and Around Maidenhead</h2>
            <p>
              Whether you stay in central Maidenhead, along the river or near Heathrow, we
              provide door-to-door taxi transfers 24/7.
            </p>
          </header>
          <div className="maidenhead-hotels-grid">
            <article className="maidenhead-hotel-card">
              <h3>Maidenhead Town & Riverside Hotels</h3>
              <p>
                A mix of chain and independent hotels, ideal for business travellers and
                weekend breaks.
              </p>
              <ul>
                <li>📍 Short hops between station, hotels and restaurants</li>
                <li>🛫 Direct taxis to Heathrow and other airports</li>
                <li>💷 Typical Maidenhead → Heathrow fare: £45–£65</li>
              </ul>
            </article>
            <article className="maidenhead-hotel-card">
              <h3>Heathrow Perimeter Hotels</h3>
              <p>
                Many Maidenhead passengers stay near Heathrow for early flights. We connect
                Maidenhead and airport hotels.
              </p>
              <ul>
                <li>📍 Around 20–35 minutes by taxi</li>
                <li>💷 Typical fare: £40–£60 (saloon)</li>
                <li>🚐 Minibus upgrades available for families & groups</li>
              </ul>
            </article>
          </div>
          <p className="maidenhead-hotels-link">
            For a detailed list of airport hotels, visit our{" "}
            <Link to="/heathrow-hotels">Heathrow hotels taxi guide</Link>.
          </p>
        </section>

        {/* FAQ TEASER */}
        <section className="maidenhead-section" id="maidenhead-faq">
          <header className="maidenhead-section-header">
            <h2>❓ Maidenhead Taxi FAQs</h2>
            <p>
              A few common questions passengers ask when travelling between Maidenhead,
              Heathrow and other airports.
            </p>
          </header>
          <div className="maidenhead-faq-grid">
            <article className="maidenhead-faq-card">
              <h3>Do you pick up from Maidenhead station?</h3>
              <p>
                Yes – we regularly collect from Maidenhead station, especially for airport
                transfers and hotel connections.
              </p>
            </article>
            <article className="maidenhead-faq-card">
              <h3>Can you handle early morning Heathrow flights?</h3>
              <p>
                Yes – CheapCabGrab runs 24/7. Early-morning pickups from Maidenhead for
                Heathrow and other airports are common.
              </p>
            </article>
            <article className="maidenhead-faq-card">
              <h3>Do you offer larger vehicles for groups?</h3>
              <p>
                Yes – MPVs, 7–9 seaters and 14+ seater vehicles are available. For bigger
                groups see our <Link to="/minibus">minibus</Link> and{" "}
                <Link to="/coach">coach hire</Link> pages.
              </p>
            </article>
          </div>
          <p className="maidenhead-faq-link">
            See all questions in our full <Link to="/faq">CheapCabGrab FAQ guide</Link>.
          </p>
        </section>

        {/* SUPPORT SECTION */}
        <section className="maidenhead-section" id="maidenhead-support">
          <header className="maidenhead-section-header">
            <h2>📞 Need help with a Maidenhead taxi booking?</h2>
          </header>
          <p>
            Our team is available 24/7 for airport transfers, business travel, local journeys
            and special events around Maidenhead, Bray, Cookham and Taplow.
          </p>
          <p>
            Contact us via our <Link to="/contact">support page</Link> or email
            <strong> support@cheapcabgrab.com</strong>.
          </p>
        </section>

        <div className="route-bottom-cta">
          <button className="route-bottom-cta-btn" onClick={scrollToForm}>
            Book Maidenhead Taxi
          </button>
        </div>
      </main>

      <FloatingSupportChat
        brand="ccg"
        defaultFlow="enquiry"
        position="right"
        showAfterMs={9000}
        nudgeText="Need Maidenhead taxi help?"
      />
    </div>
  );
};

export default MaidenheadTaxi;
